import { useState, useEffect } from 'react';
import { apiGetAllProjects, apiGetCustomers, apiCreateProject } from './api';
import { formatDate } from './dateUtils';

const STATUS_CONFIG = {
  active:   { label: 'Active',    color: 'var(--success)',  bg: 'var(--success-bg)'  },
  review:   { label: 'In Review', color: 'var(--warning)',  bg: 'var(--warning-bg)'  },
  inactive: { label: 'Inactive',  color: 'var(--error)',    bg: 'var(--error-bg)'    },
};

export default function AdminProjectsPage() {
  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');
  const [showModal,    setShowModal]    = useState(false);

  function reload() {
    apiGetAllProjects()
      .then(setProjects)
      .catch(() => setError('Failed to load projects.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { reload(); }, []);

  const filtered = projects.filter(p => {
    const q = search.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.customer?.toLowerCase().includes(q)
    );
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Projects</h1>
          <p style={styles.subtitle}>{projects.length} total projects</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              style={styles.searchInput}
              type="text"
              autoComplete="off"
              placeholder="Search by name or customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button style={styles.addBtn} onClick={() => setShowModal(true)}>
            + New Project
          </button>
        </div>
      </div>

      {showModal && (
        <NewProjectModal
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); setLoading(true); reload(); }}
        />
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner" />
          <p>Loading projects…</p>
        </div>
      )}

      {error && (
        <div className="error-alert">{error}</div>
      )}

      {!loading && !error && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['ID', 'Name', 'Customer', 'Status', 'Standards', 'Source Files', 'Started', 'Updated'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const status = STATUS_CONFIG[p.status] ?? STATUS_CONFIG.active;
                return (
                  <tr key={p.id} style={styles.tr}>
                    <td style={styles.td}><span style={styles.idBadge}>#{p.id}</span></td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{p.name}</td>
                    <td style={styles.td}>{p.customer}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: status.color, background: status.bg }}>
                        {status.label}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{p.standardsCount}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{p.sourceFiles}</td>
                    <td style={styles.td}>{formatDate(p.startDate)}</td>
                    <td style={styles.td}>{p.updatedAt ? formatDate(p.updatedAt) : <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div style={styles.empty}>No projects match "{search}"</div>
          )}
        </div>
      )}
    </div>
  );
}

function NewProjectModal({ onClose, onSave }) {
  const [customers,   setCustomers]   = useState([]);
  const [customerId,  setCustomerId]  = useState('');
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate,   setStartDate]   = useState('');
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState('');

  useEffect(() => {
    apiGetCustomers()
      .then(list => {
        setCustomers(list);
        if (list.length > 0) setCustomerId(String(list[0].customerId));
      })
      .catch(() => setErr('Could not load customers.'));
  }, []);

  async function handleSave() {
    if (!projectName.trim()) { setErr('Project name is required.'); return; }
    if (!customerId)          { setErr('Please select a customer.');  return; }
    setSaving(true); setErr('');
    try {
      await apiCreateProject({ name: projectName.trim(), customerId: Number(customerId), description, startDate });
      onSave();
    } catch (e) {
      setErr(e.message); setSaving(false);
    }
  }

  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={m.modal}>
        <div style={m.header}>
          <h2 style={m.title}>New Project</h2>
          <button style={m.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={m.body}>
          {err && <div className="error-alert" style={{ marginBottom: 12 }}>{err}</div>}

          <div style={m.field}>
            <label style={m.label}>Customer *</label>
            <select style={m.input} value={customerId} onChange={e => setCustomerId(e.target.value)}>
              {customers.length === 0 && <option value="">Loading…</option>}
              {customers.map(c => (
                <option key={c.customerId} value={c.customerId}>
                  {c.companyName || c.legalName} (#{c.customerId})
                </option>
              ))}
            </select>
          </div>

          <div style={m.field}>
            <label style={m.label}>Project Name *</label>
            <input
              style={m.input}
              type="text"
              placeholder="e.g. Acme CRM Migration"
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
            />
          </div>

          <div style={m.field}>
            <label style={m.label}>Start Date</label>
            <input
              style={m.input}
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>

          <div style={m.field}>
            <label style={m.label}>Description</label>
            <textarea
              style={{ ...m.input, resize: 'vertical', minHeight: 80 }}
              placeholder="Brief description of the project…"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div style={m.footer}>
          <button style={m.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={m.saveBtn}   onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

const m = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    backdropFilter: 'blur(2px)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
    width: '100%', maxWidth: 480, maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-family)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 22px 14px', borderBottom: '1px solid var(--border)',
  },
  title:    { fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', margin: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 },
  body:     { padding: '18px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 },
  field:    { display: 'flex', flexDirection: 'column', gap: 5 },
  label:    { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' },
  input: {
    padding: '8px 11px', borderRadius: 7,
    border: '1.5px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none',
    fontFamily: 'var(--font-family)', width: '100%', boxSizing: 'border-box',
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid var(--border)',
  },
  cancelBtn: {
    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    border: '1.5px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-secondary)', cursor: 'pointer',
  },
  saveBtn: {
    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
    border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer',
  },
};

const styles = {
  page:    { padding: 'clamp(16px, 3vw, 28px) clamp(12px, 3vw, 32px)', fontFamily: 'var(--font-family)' },
  header:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16, flexWrap: 'wrap' },
  title:   { fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.4px' },
  subtitle:{ fontSize: 13, color: 'var(--text-muted)', margin: 0 },

  addBtn: {
    background: 'var(--primary)', color: '#fff',
    border: 'none', borderRadius: 8,
    padding: '9px 18px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  searchWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: 11, fontSize: 13, pointerEvents: 'none' },
  searchInput: {
    padding: '8px 14px 8px 34px', borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--border)', background: 'var(--bg-card)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%', maxWidth: 260,
    fontFamily: 'var(--font-family)',
  },

  tableWrap: {
    background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)', overflow: 'hidden',
    boxShadow: 'var(--shadow-card)',
  },
  table:   { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 10,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px',
    color: 'var(--text-muted)', background: 'var(--bg-table-header)',
    borderBottom: '1px solid var(--border)',
  },
  tr: { borderBottom: '1px solid var(--border)', transition: 'background 0.12s' },
  td: { padding: '11px 14px', fontSize: 13, color: 'var(--text-primary)', verticalAlign: 'middle' },

  idBadge: {
    background: 'var(--bg-table-row-alt)', borderRadius: 5,
    padding: '2px 7px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
  },
  badge: {
    display: 'inline-block', padding: '3px 9px',
    borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700,
  },
  empty: {
    padding: '40px', textAlign: 'center',
    fontSize: 14, color: 'var(--text-muted)',
  },
};
